import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { ApiService } from '../../core/services/api.service';
import { CardProgram } from '../../core/models/api.models';

@Component({
  selector: 'app-card-programs',
  standalone: true,
  imports: [ReactiveFormsModule, DatePipe],
  templateUrl: './card-programs.component.html',
  styleUrl: './card-programs.component.scss',
})
export class CardProgramsComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly fb = inject(FormBuilder);

  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly success = signal<string | null>(null);
  readonly rows = signal<CardProgram[]>([]);
  readonly showCreate = signal(false);

  readonly form = this.fb.nonNullable.group({
    type: ['VIRTUAL', Validators.required],
    partner_product: ['KOBBLE_DEBIT_1', Validators.required],
    name: ['Default Virtual', Validators.required],
    is_default: [true],
  });

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.api.getCardPrograms().subscribe({
      next: (rows) => {
        this.rows.set(Array.isArray(rows) ? rows : []);
        this.loading.set(false);
      },
      error: (err: { message?: string }) => {
        this.error.set(err?.message || 'Failed to load card programs');
        this.loading.set(false);
      },
    });
  }

  openCreate(): void {
    this.showCreate.set(true);
    this.success.set(null);
    this.error.set(null);
  }

  cancelCreate(): void {
    this.showCreate.set(false);
  }

  create(): void {
    if (this.form.invalid || this.saving()) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving.set(true);
    this.error.set(null);
    this.success.set(null);
    const value = this.form.getRawValue();

    this.api
      .createCardProgram({
        type: value.type,
        partner_product: value.partner_product,
        name: value.name,
        is_default: value.is_default ? 1 : 0,
      })
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.showCreate.set(false);
          this.success.set('Card program created');
          this.load();
        },
        error: (err: { message?: string }) => {
          this.saving.set(false);
          this.error.set(err?.message || 'Failed to create card program');
        },
      });
  }

  sync(): void {
    if (this.saving()) return;
    this.saving.set(true);
    this.error.set(null);
    this.success.set(null);
    this.api.syncCardPrograms().subscribe({
      next: () => {
        this.saving.set(false);
        this.success.set('Card programs synced from Kobble');
        this.load();
      },
      error: (err: { message?: string }) => {
        this.saving.set(false);
        this.error.set(err?.message || 'Failed to sync card programs');
      },
    });
  }

  setDefault(row: CardProgram): void {
    if (this.saving() || !row.id) return;
    this.saving.set(true);
    this.error.set(null);
    this.success.set(null);
    this.api.setDefaultCardProgram(row.id).subscribe({
      next: () => {
        this.saving.set(false);
        this.success.set(`Program #${row.id} set as default`);
        this.load();
      },
      error: (err: { message?: string }) => {
        this.saving.set(false);
        this.error.set(err?.message || 'Failed to set default program');
      },
    });
  }

  isDefault(row: CardProgram): boolean {
    return row.is_default === 1 || row.is_default === true;
  }
}
